import React from "react";
import { Link } from "react-router-dom";
import "./Services.css";
import useRevealOnIntersect from "../hooks/useRevealOnIntersect";

const services = [
  {
    key: "security",
    title: "Security",
    description:
      "Advanced surveillance, AI-powered threat detection, and smart safety systems for a secure digital and physical environment.",
    basePath: "/security",
    label: "Protection",
    highlights: ["AI analytics", "Access control", "Surveillance systems"],
  },
  {
    key: "network",
    title: "Network",
    description:
      "Seamless IoT integration, fiber optic deployments, and digital transformation to keep your enterprise connected.",
    basePath: "/network",
    label: "Connectivity",
    highlights: ["Wi-Fi 6/6E", "SD-WAN", "Structured cabling"],
  },
  {
    key: "it-infrastructure",
    title: "IT Infrastructure",
    description:
      "Robust IT setups including access control, power-efficient devices, and solar-powered infrastructure solutions.",
    basePath: "/it-infrastructure",
    label: "Core Systems",
    highlights: ["Servers and storage", "Backup and resilience", "Identity and access"],
  },
  {
    key: "general-order-supply",
    title: "General Order Supply",
    description:
      "Emphasizes reliable sourcing and delivery of quality equipment, tools, and materials for every industry.",
    basePath: "/general-order-supply",
    label: "Procurement",
    highlights: ["Vetted sourcing", "QA and documentation", "Delivery coordination"],
  },
];

const icons = {
  security: "🛡️",
  network: "📡",
  "it-infrastructure": "🧩",
  "general-order-supply": "📦",
};

export default function Services() {
  useRevealOnIntersect(".svc-reveal", { threshold: 0.18 });

  return (
    <section id="services" className="svc">
      <div className="svc-bg" aria-hidden="true">
        <span className="svc-blob b1" />
        <span className="svc-blob b2" />
        <span className="svc-blob b3" />
        <span className="svc-grid-glow" />
      </div>

      <div className="svc-container">
        <header className="svc-header svc-reveal" data-reveal="up" style={{ "--d": "40ms" }}>
          <span className="svc-pill">Our Services</span>
          <h2>Build securely. Scale confidently.</h2>
          <p className="svc-subtitle">
            A focused portfolio covering protection, connectivity, infrastructure, and supply with delivery built for measurable operations.
          </p>
        </header>

        <div className="svc-overview svc-reveal" data-reveal="up" style={{ "--d": "80ms" }}>
          <div className="svc-overview-card">
            <strong>Strategy to execution</strong>
            <span>Assessment, design, implementation, validation, and support handled in one clear delivery flow.</span>
          </div>
          <div className="svc-overview-card">
            <strong>Designed for real operations</strong>
            <span>Solutions shaped around uptime, maintainability, user adoption, and long-term supportability.</span>
          </div>
        </div>

        <div className="svc-grid">
          {services.map(({ key: serviceKey, title, description, basePath, label, highlights }, i) => (
            <article
              key={serviceKey}
              className="svc-card hover-tilt svc-reveal"
              data-reveal="up"
              style={{ "--d": `${120 + i * 70}ms` }}
            >
              <div className="svc-card-meta">
                <span className="svc-badge">{label}</span>
                <span className="svc-step">0{i + 1}</span>
              </div>

              <div className="svc-card-head">
                <div className="svc-icon">{icons[serviceKey] || "🛠️"}</div>
                <h3 className="svc-title">{title}</h3>
              </div>

              <p className="svc-desc">{description}</p>

              <ul className="svc-highlights">
                {highlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>

              <div className="svc-actions" aria-label={`${title} quick links`}>
                <Link
                  to={basePath}
                  className="svc-btn svc-btn-primary"
                  aria-label={`View ${title} service details`}
                >
                  View Service
                  <span className="shine" aria-hidden="true" />
                </Link>
                <Link
                  to={`${basePath}#contact`}
                  className="svc-btn svc-btn-secondary"
                  aria-label={`Contact Nexite about ${title}`}
                >
                  Talk to Us
                  <span className="chev" aria-hidden="true">›</span>
                </Link>
              </div>

              <span className="card-shine" aria-hidden="true" />
            </article>
          ))}
        </div>
      </div>

      <div className="svc-accent" aria-hidden="true" />
    </section>
  );
}